// src/theme.js
// Tema global ConectaChurch — paleta do template aprovado (landing).
// Fundo escuro #05080F, base surfaces #0A141C/#0D1B24, acento teal #00F1A7,
// secundário lime #B4F300, raio máximo 5px, fontes Plus Jakarta Sans / Inter.
import { createTheme } from '@mui/material/styles';

const TEAL     = '#00F1A7';
const TEAL_D   = '#00C48C';
const TEAL_L   = '#6FF5CF';
const LIME     = '#B4F300';
const LIME_D   = '#98D200';
const LIME_L   = '#CDFF3D';
const BG       = '#05080F';
const BG_2     = '#071219';
const SURFACE  = '#0A141C';
const SURFACE2 = '#0D1B24';
const BORDER   = 'rgba(0, 241, 167, 0.12)';
const BORDER_S = 'rgba(0, 241, 167, 0.22)';
const TEXT     = '#E9F1EF';
const MUTED    = '#93A7A3';
const DISABLED = '#5E6E6A';
const CONTRAST = '#0B1200';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: TEAL, dark: TEAL_D, light: TEAL_L, contrastText: CONTRAST },
    secondary:  { main: LIME, dark: LIME_D, light: LIME_L, contrastText: CONTRAST },
    background: { default: BG, paper: SURFACE },
    text:       { primary: TEXT, secondary: MUTED, disabled: DISABLED },
    divider:    BORDER,
    success:    { main: '#18D7A0' },
    error:      { main: '#FF5C5C' },
    warning:    { main: '#FFC24B' },
    info:       { main: TEAL },
    action: {
      hover:    'rgba(0, 241, 167, 0.10)',
      selected: 'rgba(0, 241, 167, 0.16)',
    },
  },
  spacing: 8,
  shape: { borderRadius: 5 },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: { fontSize: '2rem',   fontWeight: 800, color: TEXT, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
    h2: { fontSize: '1.5rem', fontWeight: 700, color: TEXT, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
    h3: { fontSize: '1.25rem',fontWeight: 700, color: TEXT, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
    h4: { fontSize: '1rem',   fontWeight: 700, color: TEXT, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
    h5: { fontSize: '0.9rem', fontWeight: 700, color: TEXT, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
    h6: { fontSize: '0.8rem', fontWeight: 700, color: TEXT, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
    body1:  { fontSize: '0.95rem', color: TEXT },
    body2:  { fontSize: '0.85rem', color: MUTED },
    caption:{ fontSize: '0.75rem', color: DISABLED },
    button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0.3 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: BG,
          color: TEXT,
          '-webkit-font-smoothing': 'antialiased',
        },
        '*': { boxSizing: 'border-box' },
        '::-webkit-scrollbar':       { width: 6 },
        '::-webkit-scrollbar-track': { background: BG_2 },
        '::-webkit-scrollbar-thumb': { background: 'rgba(0, 241, 167, 0.25)', borderRadius: 5 },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
          backgroundColor: BG,
          color: TEXT,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 20,
          paddingRight: 20,
          fontSize: '0.875rem',
        },
        containedPrimary: {
          backgroundColor: TEAL,
          color: CONTRAST,
          '&:hover': { backgroundColor: TEAL_D, boxShadow: '0 4px 16px rgba(0, 241, 167, 0.25)' },
        },
        outlinedPrimary: {
          borderColor: BORDER_S,
          color: TEAL,
          '&:hover': { backgroundColor: 'rgba(0, 241, 167, 0.08)', borderColor: TEAL },
        },
        containedSecondary: {
          backgroundColor: LIME,
          color: CONTRAST,
          '&:hover': { backgroundColor: LIME_D },
        },
        text: {
          color: MUTED,
          '&:hover': { backgroundColor: 'rgba(0, 241, 167, 0.08)', color: TEXT },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          backgroundImage: 'none',
          backgroundColor: SURFACE,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.45)',
          border: `1px solid ${BORDER}`,
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': { boxShadow: '0 10px 28px rgba(0, 0, 0, 0.55)' },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: 'rgba(0, 241, 167, 0.06)' } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 5, backgroundImage: 'none', color: TEXT },
        elevation1: { boxShadow: '0 2px 12px rgba(0, 0, 0, 0.45)' },
        elevation2: { boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: '#071219',
            '& .MuiInputBase-input': { backgroundColor: 'transparent', color: TEXT },
            '& fieldset': { borderColor: BORDER },
            '&:hover fieldset': { borderColor: BORDER_S },
            '&.Mui-focused fieldset': { borderColor: '#6B7280', borderWidth: 2 },
          },
          '& label': { color: MUTED },
          '& label.Mui-focused': { color: TEAL },
          '& .MuiInputBase-input': { color: TEXT },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 5, fontWeight: 600, fontSize: '0.75rem' },
        colorPrimary: { backgroundColor: 'rgba(0, 241, 167, 0.16)', color: TEAL },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: BORDER } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { color: TEXT, borderBottom: `1px solid ${BORDER}`, fontSize: '0.85rem' },
        head: { backgroundColor: SURFACE2, color: TEXT, fontWeight: 700, whiteSpace: 'nowrap', borderBottom: `1px solid ${BORDER_S}` },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          overflow: 'auto',
          backgroundColor: SURFACE,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 241, 167, 0.14)',
            '&:hover': { backgroundColor: 'rgba(0, 241, 167, 0.20)' },
          },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', backgroundColor: SURFACE, color: TEXT },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundImage: 'none', backgroundColor: SURFACE, color: TEXT },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: { backgroundColor: 'rgba(0, 241, 167, 0.12)', color: '#D1FAE5' },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: { backgroundColor: BG, '& .Mui-selected': { color: TEAL } },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { paddingLeft: 16, paddingRight: 16 },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.65rem' },
      },
    },
  },
});

export default theme;
